import { Box, Spinner } from '@kluein/klue-ui';
import { Button, Paper, Stack } from '@mantine/core';
import {
  CalendarBlankIcon,
  CaretDownIcon,
  CaretUpIcon,
  EnvelopeSimpleIcon,
  ExportIcon,
  EyeIcon,
  NotePencilIcon,
} from '@phosphor-icons/react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCardScheduleByAnswerId } from 'hooks/useCardSchedule';
import { Chat } from 'modules/shared/libs/chat/components/Chat';
import CitationItem from 'modules/shared/libs/chat/components/CitationItem';
import CopyMessageAction from 'modules/shared/libs/chat/components/CopyMessageAction';
import MessageActions from 'modules/shared/libs/chat/components/MessageActions';
import ToolCallIndicator from 'modules/shared/libs/chat/components/ToolCallIndicator';
import {
  getCitationTypeIcon,
  getEffectiveCitationTitle,
  parseIndexerUrl,
} from 'modules/shared/libs/chat/utils/sources.utils';
import TRACKING_IDS from 'tracking-ids';

import type { Icon } from '@phosphor-icons/react';
import type { UIMessage } from 'ai';
import type { MessageActionsContext } from 'modules/shared/libs/chat/components/ChatStream';
import type {
  ChatStatus,
  ChatUIMessage,
  CitationSource,
} from 'modules/shared/libs/chat/types';
import type { ToolFriendlyNamesMap } from 'modules/shared/libs/chat/utils/tool-calling-utils';
import type { ReactNode } from 'react';

type ResearchChatProps = {
  messages: ChatUIMessage[];
  status: ChatStatus;
  error?: Error;
  regenerate: () => void;
  stop: () => void;
  sessionId: string;
  isLoadingHistory: boolean;
  headerHeight?: number;
  bodyPadding?: number;
  onCreateCard?: (
    messageId: string,
    messageText: string,
    sources: CitationSource[],
    userQuestion?: string,
  ) => void;
  // Materializes a digest item directly from the answer (no card intermediary).
  // Independent of Create Card — visible whenever provided. Parent gates on
  // curator access before wiring this in.
  onAddToDigest?: (
    messageId: string,
    messageText: string,
    sources: CitationSource[],
    userQuestion?: string,
  ) => void;
  linkedCardIds?: Record<string, number>;
  onViewCard?: (cardId: number) => void;
  onScheduleCard?: (cardId: number) => void;
  onExport?: (
    messageId: string,
    messageText: string,
    sources: CitationSource[],
    userQuestion?: string,
  ) => void;
  toolFriendlyNames?: ToolFriendlyNamesMap;
};

type ActionButtonProps = {
  icon: Icon;
  label: string;
  onClick?: () => void;
  trackingId?: string;
};

function ActionButton({
  icon: IconComponent,
  label,
  onClick,
  trackingId,
}: ActionButtonProps): ReactNode {
  return (
    <Button
      variant="transparent"
      color="blue"
      size="compact-sm"
      leftSection={<IconComponent size={16} />}
      onClick={onClick}
      title={label}
      data-tracking-id={trackingId}
    >
      {label}
    </Button>
  );
}

function CardActions({
  messageId,
  messageText,
  sources,
  userQuestion,
  onCreateCard,
  onAddToDigest,
  linkedCardId,
  onViewCard,
  onScheduleCard,
}: {
  messageId: string;
  messageText: string;
  sources: CitationSource[];
  userQuestion?: string;
  onCreateCard?: ResearchChatProps['onCreateCard'];
  onAddToDigest?: ResearchChatProps['onAddToDigest'];
  linkedCardId?: number;
  onViewCard?: (cardId: number) => void;
  onScheduleCard?: (cardId: number) => void;
}) {
  // Look up persisted card link — always query so cache invalidation on
  // card deletion correctly reverts the button from "View Card" to "Create Card"
  const { linkedCardId: persistedLinkedCardId } = useCardScheduleByAnswerId(
    onCreateCard ? messageId : undefined,
  );
  const effectiveLinkedCardId = persistedLinkedCardId ?? linkedCardId;

  return (
    <>
      {onCreateCard && !effectiveLinkedCardId && (
        <ActionButton
          icon={NotePencilIcon}
          label="Create Card"
          onClick={() =>
            onCreateCard(messageId, messageText, sources, userQuestion)
          }
        />
      )}
      {effectiveLinkedCardId && onViewCard && (
        <ActionButton
          icon={EyeIcon}
          label="View Card"
          onClick={() => onViewCard(effectiveLinkedCardId)}
        />
      )}
      {effectiveLinkedCardId && onScheduleCard && (
        <ActionButton
          icon={CalendarBlankIcon}
          label="Schedule"
          onClick={() => onScheduleCard(effectiveLinkedCardId)}
        />
      )}
      {onAddToDigest && (
        <ActionButton
          icon={EnvelopeSimpleIcon}
          label="Add to Digest"
          trackingId={TRACKING_IDS.askKlue.answer.addToDigestButton}
          onClick={() =>
            onAddToDigest(messageId, messageText, sources, userQuestion)
          }
        />
      )}
    </>
  );
}

function ResearchMessageActions({
  context,
  onCreateCard,
  onAddToDigest,
  linkedCardId,
  onViewCard,
  onScheduleCard,
  onExport,
}: {
  context: MessageActionsContext;
  onCreateCard?: ResearchChatProps['onCreateCard'];
  onAddToDigest?: ResearchChatProps['onAddToDigest'];
  linkedCardId?: number;
  onViewCard?: (cardId: number) => void;
  onScheduleCard?: (cardId: number) => void;
  onExport?: ResearchChatProps['onExport'];
}): ReactNode {
  const [sourcesExpanded, setSourcesExpanded] = useState(false);

  const hasSources = context.sources.length > 0;

  const citationItems = useMemo(() => {
    return context.sources.map((source) => {
      const parsedUrl = parseIndexerUrl(source.url);
      const IconComponent = getCitationTypeIcon(parsedUrl?.type || 'url');
      const effectiveTitle = getEffectiveCitationTitle(source);
      const snippetId =
        parsedUrl?.indexerType === 'curated-snippets'
          ? parsedUrl?.id || null
          : null;

      return {
        key: source.url,
        source,
        icon: IconComponent,
        title: effectiveTitle,
        snippetId,
      };
    });
  }, [context.sources]);

  const handleToggleSources = useCallback(() => {
    setSourcesExpanded((prev) => !prev);
  }, []);

  return (
    <>
      <MessageActions isHovered={context.isHovered}>
        {hasSources && (
          <ActionButton
            icon={sourcesExpanded ? CaretUpIcon : CaretDownIcon}
            label={`Sources (${context.sources.length})`}
            onClick={handleToggleSources}
          />
        )}
        <CopyMessageAction
          messageText={context.messageText}
          sources={context.sources}
          sessionId={context.sessionId}
          messageId={context.messageId}
          getRenderedHtml={context.getRenderedHtml}
        />
        <CardActions
          messageId={context.messageId}
          messageText={context.messageText}
          sources={context.sources}
          userQuestion={context.userQuestion}
          onCreateCard={onCreateCard}
          onAddToDigest={onAddToDigest}
          linkedCardId={linkedCardId}
          onViewCard={onViewCard}
          onScheduleCard={onScheduleCard}
        />
        {onExport && (
          <ActionButton
            icon={ExportIcon}
            label="Export"
            onClick={() =>
              onExport(
                context.messageId,
                context.messageText,
                context.sources,
                context.userQuestion,
              )
            }
          />
        )}
      </MessageActions>

      {sourcesExpanded && hasSources && (
        <Stack gap="xs" bg="gray.0" p="md" bdrs="lg">
          {citationItems.map((item, index) => (
            <CitationItem
              key={item.key}
              source={item.source}
              icon={item.icon}
              title={item.title}
              snippetId={item.snippetId}
              number={index + 1}
            />
          ))}
        </Stack>
      )}
    </>
  );
}

export function ResearchChat({
  messages,
  status,
  error,
  regenerate,
  stop,
  sessionId,
  isLoadingHistory,
  headerHeight = 80,
  bodyPadding = 16,
  onCreateCard,
  onAddToDigest,
  linkedCardIds,
  onViewCard,
  onScheduleCard,
  onExport,
  toolFriendlyNames,
}: ResearchChatProps) {
  const { t } = useTranslation(['Dashboard']);

  const renderMessageActions = useCallback(
    (context: MessageActionsContext): ReactNode | null => (
      <ResearchMessageActions
        context={context}
        onCreateCard={onCreateCard}
        onAddToDigest={onAddToDigest}
        linkedCardId={linkedCardIds?.[context.messageId]}
        onViewCard={onViewCard}
        onScheduleCard={onScheduleCard}
        onExport={onExport}
      />
    ),
    [
      onCreateCard,
      onAddToDigest,
      linkedCardIds,
      onViewCard,
      onScheduleCard,
      onExport,
    ],
  );

  const renderIndicators = useCallback(
    (context: {
      message: UIMessage;
      status?: ChatStatus;
      isCurrentMessage?: boolean;
    }): ReactNode | null => (
      <ToolCallIndicator
        message={context.message}
        status={context.status}
        isCurrentMessage={context.isCurrentMessage}
        toolFriendlyNames={toolFriendlyNames}
      />
    ),
    [toolFriendlyNames],
  );

  if (isLoadingHistory) {
    return (
      <Box align="center" justify="center" fill>
        <Spinner />
      </Box>
    );
  }

  return (
    <Paper p="xl" withBorder>
      <Chat.Stream
        messages={messages}
        status={status}
        onStop={stop}
        error={error}
        regenerate={regenerate}
        sessionId={sessionId}
        headerHeight={headerHeight}
        bodyPadding={bodyPadding}
        emptyStateMessage={t('Dashboard:askKlue.researchModeEmptyState')}
        useViewportMinHeight={false}
        renderMessageActions={renderMessageActions}
        renderIndicators={renderIndicators}
      />
    </Paper>
  );
}
